// Define the interfaces locally
interface ResponseObject {
  analysis: {
    destinations?: Array<{
      name: string;
      description: string;
    }>;
  };
  weatherData: {
    current: Record<string, any>;
    historical: Record<string, any>;
  };
  tripAdvisorData: Record<string, any>;
  errors: Array<string>;
}

// Define the TravelPreferences interface
interface TravelPreferences {
  accommodation: string[] | null;
  budget: string;
  travelCompanions: string;
  description: string;
  startDate: string;
  endDate: string;
  selectedCities: string[];
  selectedCountries: string[];
  includeFlightSearch?: boolean;
}

/**
 * Gets a brief description of a city for the discover page
 * @param cityName The name of the city to describe
 * @returns A brief description of the city
 */
export const geminiService = {
  async generateTextWithFunctions(prompt: string, temperature: number = 0.7): Promise<ResponseObject> {
    // Implementation would be in the main file
    // This is just a stub that returns a basic response object
    return {
      analysis: {
        destinations: [{
          name: prompt,
          description: `Description of ${prompt}`
        }]
      },
      weatherData: {
        current: {},
        historical: {}
      },
      tripAdvisorData: {},
      errors: []
    };
  },

  async generateTravelSuggestions(preferences: TravelPreferences): Promise<any> {
    // Implementation would be in the main file
    // Return a placeholder object to satisfy TypeScript
    return {
      tripDetails: {
        startDate: preferences.startDate,
        endDate: preferences.endDate
      }
    };
  },

  async getAirportCode(cityName: string): Promise<string> {
    // Implementation would be in the main file
    // Return a placeholder string to satisfy TypeScript
    return `${cityName.substring(0, 3).toUpperCase()}`;
  },

  async getCityDescription(cityName: string): Promise<string> {
    try {
      const prompt = `Write a brief, engaging paragraph about ${cityName} as a travel destination. 
      Include information about what makes it special, its culture, attractions, and why travelers might want to visit.
      Keep it concise (about 3-4 sentences), informative, and inspiring. Write in a warm, conversational tone.`;
      
      const response = await this.generateTextWithFunctions(prompt, 0.7);
      
      // Extract a coherent paragraph from the response
      let description = '';
      if (response && typeof response === 'object') {
        // Try to extract from analysis destinations
        if (response.analysis?.destinations?.[0]?.description) {
          description = response.analysis.destinations[0].description;
        }
      }
      
      // If no useful description was found, return a simple fallback
      if (!description || description.length < 50) {
        return `Discover the unique charm and attractions of ${cityName} during your visit. This destination offers a blend of culture, history, and unforgettable experiences.`;
      }
      
      return description;
    } catch (error) {
      console.error('Error getting city description:', error);
      return `Discover the unique charm and attractions of ${cityName} during your visit.`;
    }
  },

  /**
   * Gets transportation information for a city
   * @param cityName The name of the city
   * @returns A string with bullet points about transportation in the city
   */
  async getCityTransportation(cityName: string): Promise<string> {
    try {
      console.log(`Requesting transportation info for: ${cityName}`);
      
      // Call the backend API endpoint for transportation info
      const response = await fetch(`/api/gemini/city/transportation?city=${encodeURIComponent(cityName)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Transportation API response status:', response.status);
      
      if (!response.ok) {
        console.error('API Error:', response.statusText);
        throw new Error(`Failed to fetch transportation info: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Transportation API response:', data);
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to get transportation information');
      }
      
      return data.transportation || 'No transportation information available';
    } catch (error) {
      console.error(`Error getting ${cityName} transportation info:`, error);
      throw error;
    }
  },
  
  /**
   * Gets a list of upcoming events in a city during the specified travel period
   * @param cityName The name of the city
   * @param startDate Optional start date of the travel period (ISO format)
   * @param endDate Optional end date of the travel period (ISO format)
   * @returns An array of event objects for displaying in the UI
   */
  async getCityEvents(cityName: string, startDate?: string, endDate?: string): Promise<any[]> {
    try {
      console.log(`Fetching events for ${cityName} from ${startDate || 'now'} to ${endDate || 'unspecified'}`);
      
      if (!startDate || !endDate) {
        console.warn('Missing date range for events search');
        return [];
      }
      
      // Use the new PredictHQ backend API endpoint
      const queryParams = new URLSearchParams();
      queryParams.append('city', cityName);
      queryParams.append('startDate', startDate);
      queryParams.append('endDate', endDate);
      
      const response = await fetch(`/api/predicthq/events?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        console.error(`Failed to fetch events: ${response.status} ${response.statusText}`);
        return [];
      }
      
      const data = await response.json();
      console.log('Events API response:', data);
      
      if (!data.success) {
        console.error(data.error || 'Failed to get events');
        return [];
      }
      
      return data.events || [];
    } catch (error) {
      console.error('Error getting city events:', error);
      // Return empty array on error
      return [];
    }
  }
}

// Helper function to generate a weekend date
function getWeekendDate(fromDate: Date): string {
  const weekendDate = new Date(fromDate);
  const daysUntilSaturday = (6 - weekendDate.getDay() + 7) % 7 || 7;
  weekendDate.setDate(weekendDate.getDate() + daysUntilSaturday);
  
  return `${weekendDate.toLocaleDateString('en-US', {weekday: 'long'})}, ${weekendDate.toLocaleDateString('en-US', {month: 'long', day: 'numeric', year: 'numeric'})}`;
}

// Helper function to generate a random date between two dates
function getRandomDate(start: Date, end: Date): string {
  const randomDate = new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));
  return `${randomDate.toLocaleDateString('en-US', {weekday: 'long'})}, ${randomDate.toLocaleDateString('en-US', {month: 'long', day: 'numeric', year: 'numeric'})}`;
} 